import {
  Checkbox, Divider, ListItemText, Menu, MenuItem,
} from '@mui/material';
import { FC } from 'react';
import { PlanningsGroupEntity } from 'src/entities/PlanningsGroup.entity';
import { TruckDisplayStatusVo } from 'src/vo/TruckDisplayStatusVo';

/**
 * 「すべて表示 / 配車済み車両のみ / 未配車のみ / 選択中のみ」+ グループチェックボックスを束ねた
 * 共有メニュー (Popover 中身)。trigger Button は呼び出し側で持つ。
 *
 * V3Plans の TruckSelectMenuPresenter と同じ UI 構成を持ち、ConvenienceDispatches と
 * V3Plans/V3Timeline で再利用できるようにしている。
 *
 * グループ多選択は groupEntities の中に id=0 名前="すべて表示する" の合成エントリを
 * 親側で先頭に prepend してもらう前提 (V3Plans Component.tsx と同じ方式)。
 * これにより「すべて表示する」チェックボックスは個別グループと同じ MenuItem として扱える。
 */
type Props = {
  anchorEl: HTMLElement | null;
  open: boolean;
  onClose: () => void;
  truckDisplayStatus: TruckDisplayStatusVo;
  onTruckDisplayStatusChange: (status: TruckDisplayStatusVo) => void;
  /** 「選択中のみ表示する」の活性条件 (= 行が 1 つ以上選択されている) */
  hasSelection: boolean;
  /** 先頭に id=0 「すべて表示する」を含む全グループ。groups が無いなら空配列 */
  groupEntities: PlanningsGroupEntity[];
  /** 現在チェック済みのグループ index 群 (0 = 「すべて表示する」を含む) */
  selectedGroupIndices: number[];
  onSelectedGroupIndicesChange: (next: number[]) => void;
};

const STATUS_OPTIONS: { value: TruckDisplayStatusVo; label: string }[] = [
  { value: 'すべて表示', label: 'すべて表示' },
  { value: '配車済み車両のみ表示', label: '配車済み車両のみ表示' },
  { value: '未配車の車両のみ表示', label: '未配車の車両のみ表示' },
  { value: '選択中のみ表示する', label: '選択中のみ表示する' },
];

const TruckDisplayStatusMenu: FC<Props> = ({
  anchorEl,
  open,
  onClose,
  truckDisplayStatus,
  onTruckDisplayStatusChange,
  hasSelection,
  groupEntities,
  selectedGroupIndices,
  onSelectedGroupIndicesChange,
}) => {
  // V3Plans の TruckSelectMenuPresenter#handleCheckboxChange と同じロジック:
  //   - index=0 (すべて表示する) は ON/OFF で全選択 / 全解除のトグル
  //   - 個別グループの ON は追加、OFF は本人と「すべて表示する」両方を外す
  const handleCheckboxChange = (value: number) => {
    if (value === 0) {
      onSelectedGroupIndicesChange(
        selectedGroupIndices.includes(0) ? [] : groupEntities.map((_g, i) => i),
      );
      return;
    }
    if (selectedGroupIndices.includes(value)) {
      onSelectedGroupIndicesChange(selectedGroupIndices.filter((it) => ![0, value].includes(it)));
    } else {
      onSelectedGroupIndicesChange([...selectedGroupIndices, value]);
    }
  };

  return (
    <Menu
      anchorEl={anchorEl}
      open={open}
      onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      MenuListProps={{ sx: { p: 0, m: 0 } }}
    >
      {STATUS_OPTIONS.map((opt) => (
        <MenuItem
          key={['truckDisplayStatus', opt.value].join('-')}
          selected={truckDisplayStatus === opt.value}
          disabled={opt.value === '選択中のみ表示する' && !hasSelection}
          onClick={() => {
            onTruckDisplayStatusChange(opt.value);
            onClose();
          }}
          sx={{ fontSize: '0.8rem' }}
        >
          {opt.label}
        </MenuItem>
      ))}
      {groupEntities.length > 0 && <Divider />}
      {groupEntities.map((g, index) => (
        <MenuItem
          key={['truckSelectMenu', g.id, g.name].join('-')}
          sx={{ p: 0 }}
          onClick={() => handleCheckboxChange(index)}
        >
          <Checkbox
            checked={selectedGroupIndices.includes(index)}
            onChange={(event) => event.stopPropagation()}
            value={index}
          />
          <ListItemText primary={g.name} primaryTypographyProps={{ fontSize: '0.8rem' }} />
        </MenuItem>
      ))}
    </Menu>
  );
};

export default TruckDisplayStatusMenu;
